import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'motion/react';

import {
  ArrowRight,
  BookOpen,
  CalendarBlank,
  ChatText,
  CurrencyDollar,
  Envelope,
  GearSix,
  Lightning,
  Link as LinkIcon,
  MagnifyingGlass,
  Play,
  Rocket,
  Sparkle,
  Star,
  UsersThree,
  WarningCircle
} from '@phosphor-icons/react';

const TEAL = '#00E5C4';
const CORAL = '#FF6B6B';

const CATEGORY_ICONS = {
  Rocket: Rocket,
  DollarSign: CurrencyDollar,
  Users: UsersThree,
  Zap: Lightning,
  Link: LinkIcon,
  Alert: WarningCircle,
  AlertCircle: WarningCircle
};

const HelpCentre = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [helpData, setHelpData] = useState({
    categories: [],
    featured_articles: [],
    total_articles: 0
  });
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchHelpData();
  }, []);

  const fetchHelpData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/help`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHelpData(data);
      }
    } catch (error) {
      console.error('Failed to fetch help data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/help/search?q=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (window.searchTimeout) clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => handleSearch(query), 300);
  };

  const getCategoryIcon = (iconName) => {
    const IconComponent = CATEGORY_ICONS[iconName] || BookOpen;
    return <IconComponent size={24} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto"
            style={{ borderColor: TEAL }}
          />
          <p className="mt-4 text-[#7D8896]">Loading help centre...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Help Centre - Man & Machine</title>
        <meta name="description" content="Learn how to set up your AI workers and start automating your business." />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] pt-24 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* ───── HERO ───── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16 pt-4"
          >
            <h1
              className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight"
              data-testid="help-hero-title"
            >
              Help Centre
            </h1>
            <p className="text-lg text-[#B0B8C4] mb-2 max-w-2xl mx-auto">
              Learn how to set up your AI workers and start automating your business.
            </p>
            <p className="text-sm mb-6 max-w-lg mx-auto" style={{ color: TEAL }}>
              Most users get their first worker running in under 10 minutes.
            </p>

            {/* CTAs */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#0B1120] transition-opacity hover:opacity-90"
                style={{ background: TEAL }}
                data-testid="contact-support-btn"
              >
                <ChatText size={18} />
                Contact Support
              </Link>
              <Link
                to="/feedback"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors hover:bg-[rgba(255,255,255,0.08)]"
                style={{ color: TEAL, border: `1px solid ${TEAL}40` }}
                data-testid="give-feedback-btn"
              >
                <Sparkle size={18} />
                Give Feedback
              </Link>
            </div>

            {/* Search */}
            <div className="max-w-2xl mx-auto relative" data-testid="help-search">
              <div className="relative">
                <MagnifyingGlass
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#5C6778]"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Search for help with workers, setup, integrations..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-12 pr-4 py-3.5 w-full text-base bg-[rgba(255,255,255,0.04)] border rounded-xl text-white placeholder-[#5C6778] focus:outline-none transition-colors"
                  style={{ borderColor: searchQuery ? `${TEAL}50` : 'rgba(255,255,255,0.08)' }}
                  data-testid="help-search-input"
                />
              </div>

              {/* Search Results Dropdown */}
              {searching && (
                <div className="absolute w-full mt-2 bg-[#161B26] rounded-xl border border-[rgba(255,255,255,0.08)] p-4 text-center z-10">
                  <div className="text-[#7D8896]">Searching...</div>
                </div>
              )}

              {searchResults && searchResults.length > 0 && (
                <div className="absolute w-full mt-2 bg-[#161B26] rounded-xl border border-[rgba(255,255,255,0.08)] max-h-96 overflow-y-auto z-10">
                  {searchResults.map((article) => (
                    <button
                      key={article.article_id || article.slug}
                      onClick={() => navigate(`/help/articles/${article.slug}`)}
                      className="w-full px-5 py-3.5 hover:bg-[rgba(255,255,255,0.04)] text-left border-b border-[rgba(255,255,255,0.04)] last:border-b-0 transition-colors"
                    >
                      <div className="font-medium text-white text-sm">{article.title}</div>
                      <div className="text-xs text-[#5C6778] capitalize mt-0.5">
                        {article.category.replace(/-/g, ' ')}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {searchResults && searchResults.length === 0 && (
                <div className="absolute w-full mt-2 bg-[#161B26] rounded-xl border border-[rgba(255,255,255,0.08)] p-4 text-center z-10">
                  <div className="text-[#7D8896] text-sm">
                    No articles found for &quot;{searchQuery}&quot;
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* ───── QUICK START PATH ───── */}
          {!searchQuery && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-16"
              data-testid="quick-start-section"
            >
              <div
                className="rounded-2xl p-8 border relative overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${TEAL}08 0%, transparent 60%)`,
                  borderColor: `${TEAL}20`,
                }}
              >
                <div className="flex items-center gap-2 mb-6">
                  <Rocket size={22} style={{ color: TEAL }} />
                  <h2 className="text-xl font-bold text-white">Start Here</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {[
                    {
                      step: '1',
                      label: 'Set Up Your Business Profile',
                      Icon: UsersThree,
                    },
                    {
                      step: '2',
                      label: 'Connect Your Email & Calendar',
                      Icon: Envelope,
                    },
                    {
                      step: '3',
                      label: 'Activate Your First Worker',
                      Icon: Lightning,
                    },
                    {
                      step: '4',
                      label: 'Run a Test',
                      Icon: Play,
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 rounded-xl px-4 py-3.5 border bg-[rgba(255,255,255,0.02)]"
                      style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold"
                        style={{ background: `${TEAL}18`, color: TEAL }}
                      >
                        {item.step}
                      </div>
                      <span className="text-sm text-white font-medium leading-snug">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-[#5C6778]">
                  Follow these steps to get your first result in minutes.
                </p>
              </div>
            </motion.div>
          )}

          {/* ───── FEATURED ARTICLES ───── */}
          {helpData.featured_articles.length > 0 && !searchQuery && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mb-16"
              data-testid="featured-articles-section"
            >
              <div className="flex items-center gap-2 mb-6">
                <Star size={20} style={{ color: TEAL }} />
                <h2 className="text-xl font-bold text-white">Featured Articles</h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {helpData.featured_articles.map((article) => (
                  <div
                    key={article.article_id || article.slug}
                    className="rounded-2xl p-6 border cursor-pointer transition-all hover:-translate-y-0.5 group"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      borderColor: 'rgba(255,255,255,0.06)',
                    }}
                    onClick={() => navigate(`/help/articles/${article.slug}`)}
                    data-testid={`featured-article-${article.slug}`}
                  >
                    <h3 className="text-base font-semibold text-white mb-2 leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-xs text-[#5C6778] capitalize mb-4">
                      {article.category.replace(/-/g, ' ')}
                    </p>
                    <div
                      className="flex items-center text-sm font-medium transition-colors"
                      style={{ color: TEAL }}
                    >
                      Read article
                      <ArrowRight size={16} className="ml-1 transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ───── BROWSE BY CATEGORY ───── */}
          {!searchQuery && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              data-testid="categories-section"
            >
              <h2 className="text-xl font-bold text-white mb-6">Browse by Category</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {helpData.categories.map((category) => (
                  <div
                    key={category.id}
                    className="rounded-2xl p-6 border cursor-pointer transition-all hover:-translate-y-0.5 group"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      borderColor: 'rgba(255,255,255,0.06)',
                    }}
                    onClick={() => navigate(`/help/categories/${category.slug}`)}
                    data-testid={`category-card-${category.slug}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="p-2 rounded-xl"
                          style={{ background: `${TEAL}12`, color: TEAL }}
                        >
                          {getCategoryIcon(category.icon)}
                        </div>
                        <h3 className="text-base font-semibold text-white">{category.name}</h3>
                      </div>
                      <span className="px-2 py-1 rounded-lg bg-[rgba(255,255,255,0.06)] text-[#7D8896] text-xs font-medium">
                        {category.article_count}
                      </span>
                    </div>
                    <p className="text-sm text-[#7D8896] mb-4 leading-relaxed">
                      {category.description}
                    </p>
                    <div
                      className="flex items-center text-sm font-medium transition-colors"
                      style={{ color: TEAL }}
                    >
                      View articles
                      <ArrowRight size={16} className="ml-1 transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Footer Stats */}
          <div className="mt-16 text-center text-[#5C6778] text-sm">
            <p>{helpData.total_articles} articles available to help you succeed</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpCentre;
